import {ConnectedPosition, OverlayModule} from '@angular/cdk/overlay';
import {ChangeDetectionStrategy, Component, ElementRef, inject, input, output, signal, viewChild} from '@angular/core';
import {Router} from '@angular/router';
import {OVERLAY_POSITIONS} from '../../overlay/overlay.constants';
import {OverlayPanelDirective} from '../../overlay/overlay-panel.directive';
import {ActionDropdownItem} from './action-dropdown';

/**
 * A menu of actions behind the shared pill trigger: the same 32px trigger and CDK-overlay panel as
 * `app-select-dropdown`, but nothing in it is selectable — hence no check column and a fixed
 * trigger label instead of one that reports a selection.
 *
 * Sits between the other two menus by trigger, not by contents: `app-select-dropdown` is for
 * picking a value, `app-context-menu` hangs the same kind of action list off a bare icon button.
 * This one is for a named control that opens actions, like the header's user menu.
 */
@Component({
  selector: 'app-action-dropdown',
  imports: [OverlayModule, OverlayPanelDirective],
  templateUrl: './action-dropdown.component.html',
  styleUrl: './action-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-action-dropdown'},
})
export class ActionDropdownComponent<T> {
  readonly items = input.required<ActionDropdownItem<T>[]>();
  /** The trigger's label. Fixed — a menu of actions has no current value to report. */
  readonly label = input.required<string>();
  /** Optional leading material icon on the trigger. */
  readonly icon = input<string | null>(null);
  /** Greys the trigger and blocks opening the menu. */
  readonly disabled = input(false);
  /** Overlay positions for the menu; below-first, flipping above, by default. */
  readonly positions = input<ConnectedPosition[]>(OVERLAY_POSITIONS);

  /**
   * The picked row's {@link ActionDropdownItem.value}, emitted whatever else the row does. Lets a
   * host keep its items a plain constant and act on them in one place, instead of closing over
   * itself in a per-item {@link ActionDropdownItem.callback}.
   */
  readonly itemSelect = output<T>();

  protected readonly open = signal(false);

  private readonly router = inject(Router);

  protected toggle(): void {
    if (this.disabled()) {
      return;
    }
    this.open.update(open => !open);
  }

  /**
   * The row's own callback runs before the navigation, so a row that does both acts on the page it
   * was opened from rather than on the one it lands on.
   *
   * Navigation is done here rather than with a `routerLink` on the row: the rows are `li`s, so
   * there is no href to gain, and a `[routerLink]` bound to a missing link is not inert — an
   * empty command array navigates to the current route.
   */
  protected select(item: ActionDropdownItem<T>): void {
    this.open.set(false);
    item.callback?.();
    if (item.routerLink) {
      const commands = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      void this.router.navigate(commands, {queryParams: item.queryParams});
    }
    this.itemSelect.emit(item.value);
  }
}
